import styles from "../styles/theme";
import { BADGE_TOKENS } from "../utils/constants";
import { hasAnyCertExpired } from "../utils/helpers";

// Neutral cert/role chip — every chip on the card uses this. Color no longer
// differentiates certs; the text does. ~20% smaller than the old certTag.
const certChip = {
  fontSize: 10, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.04em',
  background: BADGE_TOKENS.CERT_CHIP.bg,
  color: BADGE_TOKENS.CERT_CHIP.text,
  border: `1px solid ${BADGE_TOKENS.CERT_CHIP.border}`,
};

// Gold "documented" accent — hours verification + quality credential.
const verifiedBadge = {
  fontSize: 10, fontWeight: 'bold', padding: '3px 9px', borderRadius: 12, whiteSpace: 'nowrap',
  background: BADGE_TOKENS.VERIFIED_ACCENT.bg,
  color: BADGE_TOKENS.VERIFIED_ACCENT.text,
  border: `1px solid ${BADGE_TOKENS.VERIFIED_ACCENT.border}`,
};

// Lightweight status text — transparent, color-only (no filled hue).
const statusText = {
  fontSize: 10, padding: '3px 4px', whiteSpace: 'nowrap', fontWeight: 'bold',
};

export default function TechCard({ tech, onClick, isFreeEmployer, isUnlocked, matchData }) {
  // Free employers see only name, roles/certs, and state/region
  const displayLocation = isFreeEmployer && tech.location
    ? tech.location.split(',').pop()?.trim() || tech.location
    : tech.location;

  const expired = hasAnyCertExpired(tech);
  const best = matchData?.best;
  const hoursDocumented = tech.logbookVerificationStatus === 'verified';

  // Name the actual missing certs under the match verdict (truncate past 2).
  const missing = best?.missingRequired || [];
  let missingText = '';
  if (missing.length === 1) missingText = `Missing: ${missing[0]}`;
  else if (missing.length === 2) missingText = `Missing: ${missing[0]}, ${missing[1]}`;
  else if (missing.length > 2) missingText = `Missing: ${missing[0]}, ${missing[1]} +${missing.length - 2} more`;

  return (
    <div style={styles.jobCard} data-tech-card onClick={onClick}>
      <div style={styles.cardTop}>
        <div>
          <div style={styles.cardTitle} data-card-title>{tech.name}</div>
          <div style={styles.cardCompany} data-card-company>{displayLocation}</div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {expired && (
            <div title="One or more certifications may be expired" style={{ ...statusText, color: '#f87171' }}>
              {"⚠"} Expired cert
            </div>
          )}
          <div style={{ ...statusText, color: BADGE_TOKENS.OPEN_TO_WORK.text }}>{"●"} Open to Work</div>
          {isUnlocked && (
            <div style={{ ...statusText, color: '#4ade80' }}>{"✓"} Unlocked</div>
          )}
        </div>
      </div>

      {/* Match verdict — the card's dominant verdict. Extra breathing room,
          separated from other badges so it reads on its own. */}
      {best && (
        <div style={{ margin: '12px 0 6px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {best.hasNoRequirements ? (
              <span style={{
                fontSize: 13, fontWeight: 'bold', padding: '7px 16px', borderRadius: 6,
                background: BADGE_TOKENS.CERT_CHIP.bg, color: BADGE_TOKENS.CERT_CHIP.text,
                border: `1px solid ${BADGE_TOKENS.CERT_CHIP.border}`,
              }}>No requirements set</span>
            ) : best.meetsAllRequired ? (
              <span style={{
                fontSize: 13, fontWeight: 'bold', padding: '7px 16px', borderRadius: 6,
                background: BADGE_TOKENS.MATCH_FULL.bg, color: BADGE_TOKENS.MATCH_FULL.text,
                border: `1px solid ${BADGE_TOKENS.MATCH_FULL.border}`,
              }}>{"✓"} Meets requirements</span>
            ) : (
              <span style={{
                fontSize: 13, fontWeight: 'bold', padding: '7px 16px', borderRadius: 6,
                background: BADGE_TOKENS.MATCH_PARTIAL.bg, color: BADGE_TOKENS.MATCH_PARTIAL.text,
                border: `1px solid ${BADGE_TOKENS.MATCH_PARTIAL.border}`,
              }}>Missing {best.missingRequired.length} cert{best.missingRequired.length !== 1 ? 's' : ''}</span>
            )}
            {best.preferredPct !== null && (
              <span style={{ ...certChip, fontSize: 11, padding: '4px 10px' }}>{best.preferredPct}% preferred</span>
            )}
          </div>
          {/* Name the actual missing certs */}
          {missingText && (
            <div style={{ fontSize: 11, color: BADGE_TOKENS.CERT_CHIP.text, marginTop: 6 }}>{missingText}</div>
          )}
        </div>
      )}

      {/* Hours + verification — second in the hierarchy. */}
      <div style={styles.cardMeta}>
        <span>
          {"⏱"} {hoursDocumented && tech.logbookVerifiedHours != null
            ? `${tech.logbookVerifiedHours.toLocaleString()} hrs`
            : `${tech.uxoHours} UXO hrs`}
        </span>
        <span>{"✈️"} {tech.travel}</span>
      </div>
      {(hoursDocumented || tech.qualityCredentialStatus === 'verified' || (tech.hasLogbook && !hoursDocumented)) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          {hoursDocumented && (
            <span style={verifiedBadge}>{"✓"} Hours Documented</span>
          )}
          {tech.qualityCredentialStatus === 'verified' && (
            <span style={verifiedBadge}>QC {"✓"}</span>
          )}
          {tech.hasLogbook && !hoursDocumented && (
            <span style={{ fontSize: 10, color: BADGE_TOKENS.CERT_CHIP.text }}>{"📖"} Logbook Available</span>
          )}
        </div>
      )}

      {/* Cert chips — third. All neutral; text differentiates. */}
      <div style={styles.certTags} data-cert-tags>
        {tech.dodCerts.map(c => <span key={c} style={certChip} data-cert-tag>{c}</span>)}
        {tech.hazwoper40 && <span style={certChip} data-cert-tag>HAZWOPER 40-Hour</span>}
        {tech.diveCert && <span style={certChip} data-cert-tag>Dive Certified</span>}
        {tech.clearance && <span style={certChip} data-cert-tag>Clearance: {tech.clearanceLevel}</span>}
      </div>
      {tech.jobRolePreference === 'specific' && (tech.specificRoles || []).length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{"🎯"} Seeking Roles</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tech.specificRoles.map(role => (
              <span key={role} style={certChip}>{role}</span>
            ))}
          </div>
        </div>
      )}
      {tech.jobRolePreference === 'any' && (
        <div style={{ fontSize: 12, color: BADGE_TOKENS.OPEN_TO_WORK.text, marginTop: 8, marginBottom: 4 }}>
          {"✅"} Open to any qualifying role
        </div>
      )}

      {/* Description — last. One line, ellipsis; full text on the detail view. */}
      {!isFreeEmployer && (
        <p style={{ ...styles.techSummary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tech.summary}
        </p>
      )}
      <div style={styles.cardFooter}>
        <span style={styles.cardLink}>{isFreeEmployer ? 'View Limited Profile' : 'View Profile'} {"→"}</span>
      </div>
    </div>
  );
}
